import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { apiFetchJson } from '../../lib/apiFetch.js';
import { type PaymentAttempt, paymentAttemptForInvoice } from '../invoices/paymentAttempt.js';
import type {
  ExpenseEditRequest,
  JobBlueprintsResponse,
  JobDetailResponse,
  JobExpensesResponse,
  JobIncomeResponse,
  JobStatus,
  JobTimeEntriesResponse,
} from './contractsMirror.js';

/**
 * TanStack Query hooks for a single job's detail + its core-D1 Profitability aggregate, plus the
 * expense/income/merge mutations.
 *
 * `useJobQuery` hits `GET /api/v1/jobs/:id`, which returns the job + the server-computed
 * `profitability` aggregate (D-05 — job-financials FOLDED into Job detail). The SERVER computes
 * profit through the golden job-profit-calc from CORE-D1 inputs ONLY; the SPA renders those
 * numbers verbatim and NEVER re-aggregates or calls payments (T-22-vault).
 *
 * Every request rides `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain`, double-submit CSRF on non-safe methods). The SPA NEVER sends a tenant id
 * (the server resolves the tenant from the principal — SEC-11).
 */

export function jobQueryKey(jobId: number): readonly unknown[] {
  return ['job', jobId] as const;
}

export function useJobQuery(jobId: number) {
  return useQuery<JobDetailResponse>({
    queryKey: jobQueryKey(jobId),
    queryFn: () => apiFetchJson<JobDetailResponse>(`/api/v1/jobs/${jobId}`),
    enabled: Number.isFinite(jobId) && jobId > 0,
  });
}

export function useJobBlueprintsQuery(jobId: number) {
  return useQuery<JobBlueprintsResponse>({
    queryKey: ['job', jobId, 'blueprints'],
    queryFn: () => apiFetchJson<JobBlueprintsResponse>(`/api/v1/jobs/${jobId}/blueprints`),
    enabled: Number.isFinite(jobId) && jobId > 0,
  });
}

interface MutationResult {
  ok: boolean;
  error?: string;
}

/** Invalidate every cache entry tied to a job (detail, profitability, blueprints) + the list. */
function invalidateJob(queryClient: ReturnType<typeof useQueryClient>, jobId: number) {
  void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
  void queryClient.invalidateQueries({ queryKey: ['jobs'] });
}

export interface ExpenseInput {
  amount: number;
  date: string;
  category?: string;
  vendor?: string;
  /** Server-generated receipt filename from `POST /expenses/upload-receipt`; when
   *  present the server links the stored receipt to the new expense (22-21). */
  receiptFilename?: string;
}

/**
 * The subset of the parsed-receipt suggestions the add-expense form prefills from
 * (mirrors the 22-21 `ParsedReceipt` fields the UI uses; the SPA never recomputes).
 */
export interface ReceiptSuggestions {
  vendorName?: string;
  suggestedCategory?: string;
  total?: number;
}

/** `POST /api/v1/expenses/upload-receipt` envelope (22-21). */
export interface UploadReceiptResult {
  ok: boolean;
  receipt: {
    receiptFilename: string;
    status: string;
    hasSuggestions: boolean;
    parsed: ReceiptSuggestions | null;
    ocrEngine: string | null;
    errorMessage: string | null;
  };
}

/** `GET /api/v1/expenses/receipt-ocr-status` envelope (22-21). */
interface ReceiptOcrStatusResponse {
  ok: boolean;
  status: string;
  parsed: ReceiptSuggestions | null;
}

/**
 * Upload a receipt to the OCR pipeline (`POST /api/v1/expenses/upload-receipt`,
 * 22-21 — Admin/Manager, server-gated). The body is a `FormData` whose file field
 * is named `receipt`; the SPA never sends a tenant id (SEC-11). Returns the server
 * envelope carrying the server-generated `receiptFilename` + immediate OCR status.
 * On the Node path OCR runs inline (status `done` with `parsed`); on the Worker path
 * it enqueues and returns `pending` (poll with `pollReceiptOcr`).
 */
export function useUploadReceiptMutation() {
  return useMutation<UploadReceiptResult, Error, FormData>({
    mutationFn: (formData) =>
      apiFetchJson<UploadReceiptResult>('/api/v1/expenses/upload-receipt', {
        method: 'POST',
        body: formData,
      }),
  });
}

const OCR_POLL_INTERVAL_MS = 1500;
const OCR_POLL_MAX_ATTEMPTS = 8;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Poll `GET /api/v1/expenses/receipt-ocr-status` on an interval until the OCR status
 * is terminal (anything but `pending`) or a small attempt cap is hit, then stop.
 * Returns the parsed suggestions from the terminal poll (or null). `shouldContinue`
 * lets the caller bail early (e.g. on unmount) so no state is applied after teardown.
 */
export async function pollReceiptOcr(
  receiptFilename: string,
  shouldContinue: () => boolean = () => true,
): Promise<ReceiptSuggestions | null> {
  const query = encodeURIComponent(receiptFilename);
  for (let attempt = 0; attempt < OCR_POLL_MAX_ATTEMPTS; attempt += 1) {
    if (!shouldContinue()) return null;
    await delay(OCR_POLL_INTERVAL_MS);
    if (!shouldContinue()) return null;
    const res = await apiFetchJson<ReceiptOcrStatusResponse>(
      `/api/v1/expenses/receipt-ocr-status?receipt=${query}`,
    );
    if (res.status !== 'pending') return res.parsed;
  }
  return null;
}

/** Add an expense to a job (Admin/Manager — the server holds the gate). */
export function useAddExpenseMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, ExpenseInput>({
    mutationFn: (input) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/${jobId}/expenses`, {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      // The outstanding balance moved, so the preview behind the button is now stale.
      void queryClient.invalidateQueries({ queryKey: ['job', jobId, 'billable'] });
      invalidateJob(queryClient, jobId);
    },
  });
}

export interface IncomeInput {
  amount: number;
  date: string;
  description?: string;
  /**
   * Apply this money to one of the job's invoices, settling that bill and recording the payment.
   * Omitted or null means income only — money that never came through an invoice, which is an
   * ordinary thing to record and must stay possible.
   */
  invoiceId?: number | null;
}

export interface OpenInvoice {
  id: number;
  invoiceNumber: string | null;
  total: number;
  paid: number;
  openBalance: number;
  dueDate: string | null;
  status: string;
}

/**
 * The job's invoices that still have money owing, for the income form's picker.
 *
 * Balances come from the payments vault, so this is a server round-trip rather than something
 * derivable from the job payload already on screen.
 */
export function useJobOpenInvoicesQuery(jobId: number, enabled = true) {
  return useQuery<{ ok: boolean; invoices: OpenInvoice[] }>({
    queryKey: ['job', jobId, 'open-invoices'],
    queryFn: () =>
      apiFetchJson<{ ok: boolean; invoices: OpenInvoice[] }>(
        `/api/v1/jobs/${jobId}/open-invoices`,
      ),
    enabled: enabled && jobId > 0,
  });
}

export interface ChangeOrder {
  id: number;
  number: string;
  title: string | null;
  total: number;
  status: string;
  /**
   * Whether the job's CONTRACT has moved, which is a different question from what the customer
   * said. `status` is the customer's answer; this is the money.
   */
  appliedAt: string | null;
  signedAt: string | null;
  signerName: string | null;
  billedInvoiceId: number | null;
  createdAt: string;
}

export interface ChangeOrdersResponse {
  ok: boolean;
  changeOrders: ChangeOrder[];
  contractAmount: number;
}

/** A job's change orders, plus the contract they add up to. */
export function useJobChangeOrdersQuery(jobId: number) {
  return useQuery<ChangeOrdersResponse>({
    queryKey: ['job', jobId, 'change-orders'],
    queryFn: () => apiFetchJson<ChangeOrdersResponse>(`/api/v1/jobs/${jobId}/change-orders`),
    enabled: jobId > 0,
  });
}

export interface ChangeOrderInput {
  title?: string;
  scopeOfWork?: string;
  /** Negative is legitimate — a deductive change order for work removed or a correction. */
  total: number;
}

/** Raise a change order against a job (Admin/Manager). */
export function useCreateChangeOrderMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean; id: number; number: string }, Error, ChangeOrderInput>({
    mutationFn: (input) =>
      apiFetchJson<{ ok: boolean; id: number; number: string }>(
        `/api/v1/jobs/${jobId}/change-orders`,
        {
          method: 'POST',
          body: JSON.stringify(input),
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job', jobId, 'change-orders'] });
      invalidateJob(queryClient, jobId);
    },
  });
}

/**
 * Approve a change order internally, moving the job's contract.
 *
 * The server refuses a second apply with 409 rather than doubling the contract, so a double-click
 * is safe by construction and not by the button being disabled fast enough.
 */
export function useApproveChangeOrderMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean; contractAmount: number }, Error, { changeOrderId: number }>({
    mutationFn: ({ changeOrderId }) =>
      apiFetchJson<{ ok: boolean; contractAmount: number }>(
        `/api/v1/change-orders/${changeOrderId}/approve`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job', jobId, 'change-orders'] });
      invalidateJob(queryClient, jobId);
    },
  });
}

export interface JobBillable {
  ok: boolean;
  contractAmount: number;
  alreadyInvoiced: number;
  remaining: number;
}

/** What billing this job would ask for, so the confirm can show the arithmetic. */
export function useJobBillableQuery(jobId: number) {
  return useQuery<JobBillable>({
    queryKey: ['job', jobId, 'billable'],
    queryFn: () => apiFetchJson<JobBillable>(`/api/v1/jobs/${jobId}/billable`),
    enabled: jobId > 0,
  });
}

/** Raise a draft invoice for whatever is still outstanding on the job (Admin/Manager). */
export function useCreateInvoiceForJobMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: boolean; invoiceId: number; invoiceNumber: string; amount: number },
    Error,
    void
  >({
    mutationFn: () =>
      apiFetchJson<{ ok: boolean; invoiceId: number; invoiceNumber: string; amount: number }>(
        `/api/v1/jobs/${jobId}/create-invoice`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}

/** Add an income row to a job (Admin/Manager). */
export function useAddIncomeMutation(jobId: number) {
  const queryClient = useQueryClient();
  // ONE key per attempt, not one per call.
  //
  // Minting the key inside mutationFn produced a NEW key on every invocation, which defeats the
  // only thing the key is for: a user who resubmits after a timeout or a dropped connection sent a
  // different key, the payments RPC saw a new payment rather than a retry, and the customer's
  // invoice took the money TWICE. Held in a ref and cleared on success, exactly as AddPaymentForm
  // does — a retry of the same submission reuses the key, and the next distinct entry gets a fresh
  // one. Keyed by target invoice so switching invoices in the picker also mints a new key.
  const attemptRef = useRef<PaymentAttempt | null>(null);
  return useMutation<MutationResult, Error, IncomeInput>({
    mutationFn: (input) => {
      // 0 stands for "income only, no invoice" — that path records no payment, so the key is
      // unused; it still gets a stable value rather than a per-call random one.
      const attempt = paymentAttemptForInvoice(attemptRef.current, input.invoiceId ?? 0);
      attemptRef.current = attempt;
      return apiFetchJson<MutationResult>(`/api/v1/jobs/${jobId}/income`, {
        method: 'POST',
        body: JSON.stringify(input),
        headers: {
          'Content-Type': 'application/json',
          // Required by the server whenever this applies to an invoice, because that path records a
          // real payment through the idempotent payments RPC. Sent always — harmless when unused,
          // and one less branch that could forget it.
          'Idempotency-Key': `job-income-${jobId}-${attempt.key}`,
        },
      });
    },
    onSuccess: () => {
      attemptRef.current = null;
      invalidateJob(queryClient, jobId);
    },
  });
}

/** Archive an expense (Admin/Manager). */
export function useArchiveExpenseMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { expenseId: number }>({
    mutationFn: ({ expenseId }) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/expenses/${expenseId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}

/** Archive an income row (Admin/Manager). */
export function useArchiveIncomeMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { incomeId: number }>({
    mutationFn: ({ incomeId }) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/income/${incomeId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}

/** Merge jobs (Admin/Manager — the server re-checks the gate and tenant-scopes the merge). */
export function useMergeJobsMutation() {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { primaryJobId: number; jobIds: number[] }>({
    mutationFn: ({ primaryJobId, jobIds }) =>
      apiFetchJson<MutationResult>('/api/v1/jobs/merge', {
        method: 'POST',
        body: JSON.stringify({ primary_job_id: primaryJobId, job_ids: jobIds }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (_data, variables) => invalidateJob(queryClient, variables.primaryJobId),
  });
}

export interface UpdateJobInput {
  jobName: string | null;
  jobCode: string | null;
  clientName: string | null;
  soldBy: string | null;
  jobDescription: string | null;
  status: JobStatus | string | null;
  isOverhead: boolean;
  contractAmount: number | null;
  commissionPercent: number | null;
  retainagePercent: number | null;
  startDate: string | null;
}

/** Update a job core record and refresh job + jobs caches. */
export function useUpdateJobMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, UpdateJobInput>({
    mutationFn: (input) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/${jobId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}

export interface CreateJobInput {
  jobName: string;
  jobCode: string | null;
  clientName: string | null;
  soldBy: string | null;
  jobDescription: string | null;
  status: JobStatus | string;
  isOverhead: boolean;
  contractAmount: number | null;
  commissionPercent: number | null;
  retainagePercent: number | null;
  startDate: string | null;
}

export interface CreateJobResult extends MutationResult {
  jobId?: number;
}

/** Create a job core record (Admin/Manager — the server holds the gate + validation). */
export function useCreateJobMutation() {
  const queryClient = useQueryClient();
  return useMutation<CreateJobResult, Error, CreateJobInput>({
    mutationFn: (input) =>
      apiFetchJson<CreateJobResult>('/api/v1/jobs', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/** Archive a job (Admin/Manager). The server tenant-scopes the fetch before mutating. */
export function useArchiveJobMutation() {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { jobId: number }>({
    mutationFn: ({ jobId }) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/${jobId}/archive`, { method: 'POST' }),
    onSuccess: (_data, { jobId }) => invalidateJob(queryClient, jobId),
  });
}

/** Restore an archived job (Admin/Manager). */
export function useRestoreJobMutation() {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { jobId: number }>({
    mutationFn: ({ jobId }) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/${jobId}/restore`, { method: 'POST' }),
    onSuccess: (_data, { jobId }) => invalidateJob(queryClient, jobId),
  });
}

// ─── Job-detail row queries (22-22) — income/expense/time-entry rows for the detail tabs ──────
// Each is a coarse read (one query per job); the server carries the active rows PLUS the additive
// archived-row arrays. The SPA never re-aggregates and never sends a tenant id (SEC-11).

export function useJobIncomeQuery(jobId: number) {
  return useQuery<JobIncomeResponse>({
    queryKey: ['job', jobId, 'income'],
    queryFn: () => apiFetchJson<JobIncomeResponse>(`/api/v1/jobs/${jobId}/income`),
    enabled: Number.isFinite(jobId) && jobId > 0,
  });
}

export function useJobExpensesQuery(jobId: number) {
  return useQuery<JobExpensesResponse>({
    queryKey: ['job', jobId, 'expenses'],
    queryFn: () => apiFetchJson<JobExpensesResponse>(`/api/v1/jobs/${jobId}/expenses`),
    enabled: Number.isFinite(jobId) && jobId > 0,
  });
}

export function useJobTimeEntriesQuery(jobId: number) {
  return useQuery<JobTimeEntriesResponse>({
    queryKey: ['job', jobId, 'time-entries'],
    queryFn: () => apiFetchJson<JobTimeEntriesResponse>(`/api/v1/jobs/${jobId}/time-entries`),
    enabled: Number.isFinite(jobId) && jobId > 0,
  });
}

/** Restore an archived income row (Admin/Manager). */
export function useRestoreIncomeMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { incomeId: number }>({
    mutationFn: ({ incomeId }) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/income/${incomeId}/restore`, { method: 'POST' }),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}

/** Restore an archived expense row (Admin/Manager). */
export function useRestoreExpenseMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { expenseId: number }>({
    mutationFn: ({ expenseId }) =>
      apiFetchJson<MutationResult>(`/api/v1/jobs/expenses/${expenseId}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}

/** Edit an expense with the full field shape (PATCH /api/v1/expenses/:id — Admin/Manager). */
export function useUpdateExpenseMutation(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation<MutationResult, Error, { expenseId: number; input: ExpenseEditRequest }>({
    mutationFn: ({ expenseId, input }) =>
      apiFetchJson<MutationResult>(`/api/v1/expenses/${expenseId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateJob(queryClient, jobId),
  });
}
